import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Scale, FileText, Shield, Clock, Users,
  CheckCircle2, FolderOpen, Check, Bell, MessageSquare,
  ClipboardList, Upload, Eye, UserCheck, Lock,
} from 'lucide-react';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import shared from '@/styles/public.module.css';
import styles from '@/styles/partnerPage.module.css';

const ASSURANCES = [
  { icon: Shield, label: 'Secure & Confidential' },
  { icon: Clock, label: 'Faster Coordination' },
  { icon: Scale, label: 'Built for Attorneys' },
];

const MOCK_TILES = [
  { label: 'Pending Review', value: '5' },
  { label: 'Verified', value: '18' },
  { label: 'Active Offers', value: '7' },
];

const MOCK_CLIENTS = [
  { name: 'Sarah Martinez', type: 'Auto Accident', status: 'Needs Review', icon: Clock },
  { name: 'David Chen', type: 'Medical Malpractice', status: 'Verified', icon: CheckCircle2 },
  { name: 'Lisa Rodriguez', type: 'Slip & Fall', status: '2 Offers', icon: FileText },
];

const BENEFITS = [
  { icon: ClipboardList, title: 'Centralized Request Review', desc: 'See all client funding applications in one dashboard. No more tracking requests across emails and phone calls.' },
  { icon: Upload, title: 'Secure Document Sharing', desc: 'Upload case documents, medical records, and supporting files to a secure portal accessible only to authorized parties.' },
  { icon: MessageSquare, title: 'Cleaner Funder Communication', desc: 'Funders access case information through the platform — reducing repetitive information requests and back-and-forth.' },
  { icon: Eye, title: 'Full Funding Visibility', desc: 'Track the status of every client funding request in real time. See when offers arrive and what terms are being proposed.' },
  { icon: Bell, title: 'Automatic Notifications', desc: 'Receive instant alerts when a client applies for funding, when an offer is submitted, or when action is needed from your team.' },
  { icon: Users, title: 'Built for Firms of Any Size', desc: 'Whether you handle a few cases or hundreds, the platform scales with your practice. Manage all clients from a single account.' },
];

const STEPS = [
  { num: '1', icon: FileText, title: 'Client Submits Request', desc: 'Your client fills out a short funding application online. You are automatically notified and linked to the request.' },
  { num: '2', icon: UserCheck, title: 'Attorney Reviews & Verifies', desc: 'Review case details, confirm representation, upload supporting documents, and add attorney-only notes through your secure portal.' },
  { num: '3', icon: FileText, title: 'Funders Evaluate & Offer', desc: 'Qualified funders review the case information and submit competitive offers. You see all offers as they come in.' },
  { num: '4', icon: CheckCircle2, title: 'Client Moves Forward', desc: 'Your client reviews offers with full transparency. You maintain complete oversight and control throughout the process.' },
];

const FIGURES = [
  { value: '100%', label: 'Digital Process' },
  { value: '0', label: 'Hidden Fees' },
  { value: '24/7', label: 'Portal Access' },
];

const POINTS = [
  { title: 'Organized Case Intake', desc: 'Client applications arrive with structured fields — case type, accident details, contact info, and funding needs. No manual data entry required.' },
  { title: 'Complete Offer Transparency', desc: 'See every funding offer submitted for your clients, including advance amounts, fee structures, and terms. No hidden markups or undisclosed arrangements.' },
  { title: 'Attorney-Controlled Verification', desc: 'You decide when a case is ready for funder review. Confirm representation, add case details, and upload documents on your own timeline.' },
  { title: 'Reduced Administrative Burden', desc: 'Automated notifications, digital document sharing, and centralized status tracking eliminate the repetitive back-and-forth of traditional funding coordination.' },
  { title: 'Professional Communication Channel', desc: 'All funding-related communication flows through the platform. No more scattered email threads or lost voicemails.' },
  { title: 'Full Audit Trail', desc: 'Every action — verifications, document uploads, offer submissions — is timestamped and logged. Maintain complete records of the funding process.' },
];

const PREVIEW_APPS = [
  { name: 'Martinez, Sarah', type: 'Auto Accident', date: 'Jan 15', status: 'Needs Review' },
  { name: 'Chen, David', type: 'Med. Malpractice', date: 'Jan 14', status: 'Verified' },
  { name: 'Rodriguez, Lisa', type: 'Slip & Fall', date: 'Jan 12', status: 'Offers In' },
];

const VERIFY_TASKS = [
  { label: 'Confirm representation', done: true },
  { label: 'Upload case documents', done: true },
  { label: 'Add attorney notes', done: false },
];

const DOCUMENTS = [
  { name: 'Police_Report_Martinez.pdf', size: '2.4 MB' },
  { name: 'Medical_Records_Summary.pdf', size: '5.1 MB' },
  { name: 'Insurance_Claim_Letter.pdf', size: '890 KB' },
  { name: 'Attorney_Verification.pdf', size: '340 KB' },
];

const TRUST = [
  { icon: Lock, title: 'Secure & Confidential', desc: 'Role-based access control ensures funders, attorneys, and clients only see information they are authorized to view. All data is encrypted in transit and at rest.' },
  { icon: Scale, title: 'Transparent Funding Process', desc: 'No hidden markups, referral fees, or undisclosed arrangements. Every offer, status change, and action is visible and documented.' },
  { icon: Users, title: 'Clean Multi-Party Collaboration', desc: 'Plaintiffs, attorneys, and funders each have dedicated portals with appropriate permissions. Collaboration happens through the platform, not around it.' },
  { icon: Shield, title: 'Purpose-Built for Legal Funding', desc: 'Not a generic loan platform adapted for legal use. Every feature is designed specifically around the pre-settlement funding workflow and the needs of legal professionals.' },
];

export default function ForLawFirms() {
  usePageMeta(PAGE_META.forLawFirms);

  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="for-law-firms-page">
      <LandingHeader />

      <main>

      {/* ─── Hero ─── */}
      <section className={shared.section} data-testid="lawfirms-hero">
        <div className={`${shared.wrap} ${styles.heroGrid}`}>
          <div>
            <span className={styles.badge}>For Law Firms &amp; Attorneys</span>
            <h1 className={styles.heroTitle}>
              A Cleaner Way to Manage Client Funding Requests
            </h1>
            <p className={styles.heroSub}>
              MyCaseLoan allows law firms to share basic case information once, making it
              accessible to multiple funding providers. This streamlines the process and gives
              your clients the opportunity to review and compare their options.
            </p>
            <div className={styles.heroActions}>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnAction}`}
                onClick={() => navigate('/signup', { state: { role: 'lawyer' } })}
                data-testid="hero-join-lawfirm-btn"
              >
                Create Attorney Account <ArrowRight size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnQuiet}`}
                onClick={() => navigate('/login')}
                data-testid="hero-attorney-login-btn"
              >
                Attorney Login
              </button>
            </div>
            <ul className={styles.assurances}>
              {ASSURANCES.map((a) => (
                <li key={a.label} className={styles.assurance}>
                  <a.icon size={16} aria-hidden="true" />
                  {a.label}
                </li>
              ))}
            </ul>
          </div>

          {/* Illustrative attorney portal */}
          <div data-testid="lawfirms-hero-visual">
            <div className={styles.mock} aria-hidden="true">
              <div className={styles.mockHead}>
                <span className={styles.mockDot} />
                <span className={styles.mockDot} />
                <span className={styles.mockDot} />
                <span className={styles.mockTitle}>Attorney Portal</span>
              </div>
              <div className={styles.mockBody}>
                <div className={styles.tiles}>
                  {MOCK_TILES.map((t) => (
                    <div key={t.label} className={styles.tile}>
                      <div className={`${styles.tileValue} mcl-num`}>{t.value}</div>
                      <div className={styles.tileLabel}>{t.label}</div>
                    </div>
                  ))}
                </div>
                {MOCK_CLIENTS.map((c) => (
                  <div key={c.name} className={styles.mockRow}>
                    <div>
                      <div className={styles.rowName}>{c.name}</div>
                      <div className={styles.rowMeta}>{c.type}</div>
                    </div>
                    <span className={styles.rowStatus}>
                      <c.icon size={13} />
                      {c.status}
                    </span>
                  </div>
                ))}
                <div className={styles.mockActions}>
                  <div className={styles.mockBtn}>Review Applications</div>
                  <div className={`${styles.mockBtn} ${styles.mockBtnQuiet}`}>Upload Documents</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Benefits ─── */}
      <section
        className={`${shared.section} ${shared.sectionPanel}`}
        data-testid="lawfirms-benefits"
      >
        <div className={shared.wrap}>
          <div className={`${shared.secHead} ${styles.centreHead}`}>
            <span className={shared.eyebrow}>Why MyCaseLoan</span>
            <h2>Designed Around the Attorney Workflow</h2>
            <p>
              Manage client funding requests, verify case details, and coordinate with
              funders — all from one organized portal built for legal professionals.
            </p>
          </div>
          <div className={styles.grid3}>
            {BENEFITS.map((b) => (
              <div className={styles.card} key={b.title}>
                <div className={styles.iconBox}>
                  <b.icon size={22} aria-hidden="true" />
                </div>
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it works ─── */}
      <section className={shared.section} data-testid="lawfirms-how-it-works">
        <div className={shared.wrap}>
          <div className={`${shared.secHead} ${styles.centreHead}`}>
            <span className={shared.eyebrow}>Simple Process</span>
            <h2>From Client Request to Funded — With You in the Loop</h2>
            <p>
              MyCaseLoan keeps attorneys informed and in control at every stage of the
              pre-settlement funding process.
            </p>
          </div>
          <div className={styles.grid4}>
            {STEPS.map((s) => (
              <div className={`${styles.card} ${styles.step}`} key={s.num}>
                <div className={styles.stepNum}>{s.num}</div>
                <div className={styles.iconBox}>
                  <s.icon size={22} aria-hidden="true" />
                </div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Transparency ─── */}
      <section
        className={`${shared.section} ${shared.sectionPanel}`}
        data-testid="lawfirms-transparency"
      >
        <div className={`${shared.wrap} ${styles.split}`}>
          <div className={styles.splitHead}>
            <span className={shared.eyebrow}>Transparent &amp; Organized</span>
            <h2>Replace Fragmented Funding Coordination with a Professional System</h2>
            <p>
              No more chasing information across emails, phone calls, and fax machines.
              MyCaseLoan centralizes the entire pre-settlement funding workflow into one
              clean, auditable platform designed for legal professionals.
            </p>
            <hr className={styles.rule} />
            <div className={styles.figures}>
              {FIGURES.map((f) => (
                <div key={f.label} className={styles.figure}>
                  <div className={`${styles.figureValue} mcl-num`}>{f.value}</div>
                  <div className={styles.figureLabel}>{f.label}</div>
                </div>
              ))}
            </div>
          </div>
          <ul className={styles.checks}>
            {POINTS.map((p) => (
              <li className={styles.check} key={p.title}>
                <span className={styles.checkIcon}>
                  <Check size={13} strokeWidth={3} aria-hidden="true" />
                </span>
                <div>
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─── Portal preview ─── */}
      <section className={shared.section} data-testid="lawfirms-platform-preview">
        <div className={shared.wrap}>
          <div className={`${shared.secHead} ${styles.centreHead}`}>
            <span className={shared.eyebrow}>Platform Preview</span>
            <h2>Your Attorney Portal at a Glance</h2>
            <p>
              A dedicated workspace for reviewing client applications, managing case
              documents, and tracking funding activity across your practice.
            </p>
          </div>

          <div className={styles.grid3} aria-hidden="true">
            <div className={styles.preview}>
              <div className={styles.previewHead}>
                <ClipboardList size={16} />
                Client Applications
              </div>
              <div className={styles.previewBody}>
                {PREVIEW_APPS.map((app) => (
                  <div key={app.name} className={styles.fileRow}>
                    <div>
                      <div className={styles.rowName}>{app.name}</div>
                      <div className={styles.rowMeta}>{app.type} &middot; {app.date}</div>
                    </div>
                    <span className={styles.rowStatus}>{app.status}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.preview}>
              <div className={styles.previewHead}>
                <UserCheck size={16} />
                Case Verification
              </div>
              <div className={styles.previewBody}>
                <div className={styles.inset}>
                  <div className={styles.insetHead}>
                    <span>MCL-3051</span>
                    <span className={styles.pill}>Pending</span>
                  </div>
                  <div className={`${styles.rowMeta} ${styles.rowMetaSpaced}`}>
                    Sarah Martinez &middot; Auto Accident
                  </div>
                  <div className={styles.taskList}>
                    {VERIFY_TASKS.map((t) => (
                      <div
                        key={t.label}
                        className={`${styles.task} ${t.done ? styles.taskDone : ''}`}
                      >
                        <CheckCircle2 size={14} />
                        {t.label}
                      </div>
                    ))}
                  </div>
                </div>
                <div className={styles.mockBtn}>Complete Verification</div>
              </div>
            </div>

            <div className={styles.preview}>
              <div className={styles.previewHead}>
                <FolderOpen size={16} />
                Case Documents
              </div>
              <div className={styles.previewBody}>
                {DOCUMENTS.map((doc) => (
                  <div key={doc.name} className={styles.fileRow}>
                    <span className={styles.fileName}>
                      <FileText size={14} />
                      {doc.name}
                    </span>
                    <span className={styles.fileMeta}>{doc.size}</span>
                  </div>
                ))}
                <div className={styles.dashed}>+ Upload New Document</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Trust ─── */}
      <section
        className={`${shared.section} ${shared.sectionPanel}`}
        data-testid="lawfirms-trust"
      >
        <div className={shared.wrap}>
          <div className={`${shared.secHead} ${styles.centreHead}`}>
            <span className={shared.eyebrow}>Trust &amp; Compliance</span>
            <h2>A Platform Built for Legal Professionals</h2>
            <p>
              MyCaseLoan is designed with the security, transparency, and professionalism
              that law firms expect from any system touching client matters.
            </p>
          </div>
          <div className={styles.grid2}>
            {TRUST.map((t) => (
              <div className={`${styles.card} ${styles.trustCard}`} key={t.title}>
                <div className={styles.iconBox}>
                  <t.icon size={20} aria-hidden="true" />
                </div>
                <div>
                  <h3>{t.title}</h3>
                  <p>{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Closing CTA ─── */}
      <section
        className={`${shared.section} ${shared.sectionDark}`}
        data-testid="lawfirms-final-cta"
      >
        <div className={shared.wrap}>
          <div className={styles.finalInner}>
            <h2>Ready to Streamline Your Funding Workflow?</h2>
            <p>
              Join MyCaseLoan and give your firm a modern, organized way to manage client
              pre-settlement funding requests. Better visibility, less overhead, complete
              control.
            </p>
            <div className={styles.finalActions}>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnAccent}`}
                onClick={() => navigate('/signup', { state: { role: 'lawyer' } })}
                data-testid="final-cta-join-btn"
              >
                Create Attorney Account <ArrowRight size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnOnDark}`}
                onClick={() => navigate('/login')}
                data-testid="final-cta-attorney-login-btn"
              >
                Attorney Login
              </button>
            </div>
          </div>
        </div>
      </section>
      </main>


      <LandingFooter />
    </div>
  );
}
